import { Divider, Flex, Stack, Table, useMantineTheme } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import { Title } from '~/domains/design-system/Title';
import { Text } from '~/domains/design-system/Text';
import { UnstyledButton } from '~/domains/design-system/Button';
import { Drawer } from '~/domains/design-system/components/Drawer/Drawer';

import type { FieldDescriptor, StaticFieldProps } from '../../types/common';
import { getDependenciesValues } from '../../../context/utils/getDependencyValues';
import { getIsDependenciesSatisfied } from '../../../context/utils/getIsDependenciesSatisfied';

import type { TableModalType } from './types';
import type { TableModalValue } from './types/common';
import { createValidationSchema } from './createValidationSchema';
import { TableRow } from './TableRow';
import dt from '~/testing';

function TableModalTitle({ label }: { label?: string }) {
  return label ? (
    <Title ta="center" variant="headline-large">
      {label}
    </Title>
  ) : null;
}

function TableModalElement({
  allErrors,
  allValues,
  field,
  sectionKey,
  value,
  onValueChange,
}: StaticFieldProps<TableModalType>) {
  const [isOpen, setIsOpen] = useState(false);
  const theme = useMantineTheme();
  const options = field.data;
  const hasAllOption = options.some((option) => option.key === 'all');

  const handleValueChange = (rowKey, columnKey, newValue, skipTouched?: boolean) => {
    if (field?.disabled || !!field?.locked) return;
    const updateAllValues = (v) => {
      const allOptions = options
        .filter((option) => !option?.isDisabled)
        .map((option) => option.key);
      const allUpdatedValues = allOptions.reduce(
        (acc, key) => ({ ...acc, [key]: { [columnKey]: v } }),
        {} as Record<string, boolean>
      );
      onValueChange(allUpdatedValues, skipTouched);
    };

    if (rowKey === 'all') {
      updateAllValues(newValue);
      return;
    }

    onValueChange(
      (prev) => ({
        ...(prev ?? {}),
        ...(hasAllOption ? { all: { [columnKey]: false } } : {}),
        [rowKey]: {
          ...(prev?.[rowKey] ?? {}),
          [columnKey]: newValue,
        },
      }),
      skipTouched
    );
  };

  const isDependencySatisfied = useMemo(() => {
    if (!field.dependsOn) return true;

    const dependsOn = field.dependsOn;
    const dependencyValues = getDependenciesValues(allValues, sectionKey, dependsOn);
    const isDependant = getIsDependenciesSatisfied(
      field.key,
      dependsOn,
      allErrors,
      dependencyValues
    );

    return isDependant;
  }, [JSON.stringify(allErrors), JSON.stringify(allValues), field.key, sectionKey]);

  useEffect(() => {
    if (typeof field.defaultValues === 'undefined') return;
    if (typeof value !== 'undefined') {
      if (!isDependencySatisfied) {
        Object.keys(field.defaultValues).forEach((defaultKey) => {
          onValueChange(value ?? field?.defaultValues[defaultKey], true);
        });
      }
      return;
    }

    Object.keys(field.defaultValues).forEach((defaultKey) => {
      onValueChange(field?.defaultValues[defaultKey], true);
    });
  }, [field.defaultValue, isDependencySatisfied]);

  if (field?.locked) return null;

  return (
    <>
      <Divider />
      <Flex justify="center" /* eslint-disable-line local-rules/use-theme-tokens */ pt={14} pb={theme.other.spacing._6}>
        <UnstyledButton
          disabled={field?.disabled}
          onClick={() => setIsOpen(true)}
          data-test-id={dt.contest.create.action.editSettings}
        >
          <Text variant="label-small" c={theme.colors.blue[9]}>
            Edit settings
          </Text>
        </UnstyledButton>
      </Flex>

      <Drawer
        title={<TableModalTitle label={field?.label} />}
        onClose={() => setIsOpen(false)}
        onToggle={setIsOpen}
        open={isOpen}
      >
        {field?.description ? (
          <Stack>
            <Text c="gray2.3" ta="center" variant="body-large">
              {field.description}
            </Text>
          </Stack>
        ) : null}
        <Table data-test-id={dt.contest.create.table.container}>
          <tbody>
            {field.data.map((row) => (
              <TableRow
                columns={field.columns}
                row={row}
                key={row.key}
                onChange={handleValueChange}
                value={value as TableModalValue}
                allErrors={allErrors}
                allValues={allValues}
                sectionKey={sectionKey}
              />
            ))}
          </tbody>
        </Table>
      </Drawer>
    </>
  );
}

const Descriptor: FieldDescriptor<TableModalType> = {
  FieldComponent: TableModalElement,
  name: 'TableModal',
  createValidationSchema,
};

export default Descriptor;

export type { TableModalType };
